import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AdminAdmDefinitionService } from "../../../../api/services/admin-adm-definition.service";
import { AdminAdmTransferService } from "../../../../api/services/admin-adm-transfer.service";
import { AdmDefinitionResponse } from "../../../../api/models/adm-definition-response";
import { AdmFlattenCopyAnalysis } from "../../../../api/models/adm-flatten-copy-analysis";
import { AdmFlattenCopyAttach } from "../../../../api/models/adm-flatten-copy-attach";
import { AdmFlattenCopyGroup } from "../../../../api/models/adm-flatten-copy-group";
import { AdmFlattenCopyResult } from "../../../../api/models/adm-flatten-copy-result";
import { AdmTableColumn } from "../../../../api/models/adm-table-column";

// One chain of template columns the admin can copy, and the choices made for it.
export interface CopyRow {
  group: AdmFlattenCopyGroup;
  selected: boolean;
  expanded: boolean;
  joinIndex: number;
  connectColumn: string;
  fromPrefix: string;
  toPrefix: string;
  fieldGroup: string;
  columns: Set<string>;
  // Target columns to connect on, the likely ones first. Computed once per row/join point: a list rebuilt on every
  // change-detection pass would keep re-creating the dropdown's options and never settle.
  choices: { name: string; suggested: boolean }[];
}

// Reuse flattened columns: copy the flattened columns of a table that already has them (the template, e.g. the job
// postings) onto another table, joining the same chains of lookups. The admin picks which chains (and which of
// their columns), where the target joins each chain, and how the new columns are named; the server builds an
// import document and the shared review component previews, confirms and applies it like any other import.
@Component({
  selector: "admin-adm-flatten-copy",
  templateUrl: "./admin-adm-flatten-copy.component.html",
})
export class AdminAdmFlattenCopyComponent implements OnInit {
  // Raised after a successful apply so the Definition editor reloads, and when the admin asks to see validation.
  @Output() imported = new EventEmitter<void>();
  @Output() openValidation = new EventEmitter<void>();

  public loading = true;
  public definition: AdmDefinitionResponse = { sources: [], tables: [], columns: [], fieldGroups: [], relationships: [], filterPoints: [], issues: [] };

  public templateTable: string | null = null;
  public targetTable: string | null = null;
  // Built once (not getters): dropdown options must keep the same identity between change-detection passes.
  public templateOptions: { name: string; count: number }[] = [];
  public targetOptions: { name: string; count: number }[] = [];
  public analysis: AdmFlattenCopyAnalysis | null = null;
  public analyzing = false;
  public analyzeError = "";
  public rows: CopyRow[] = [];
  public groupSearch = "";

  public building = false;
  public buildError = "";
  public built: AdmFlattenCopyResult | null = null;
  public reviewing = false;

  public readonly stepNames = ["Select", "Preview", "Confirm", "Result"];

  constructor(private definitionService: AdminAdmDefinitionService, private transferService: AdminAdmTransferService, private snackBar: MatSnackBar) {}

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading = true;
    this.definitionService.adminAdmDefinitionGetDefinition().subscribe({
      next: (definition) => {
        this.definition = definition;
        this.loading = false;
        this.refreshTableOptions();
        if (this.templateTable === null && this.templateOptions.length > 0) this.templateTable = this.templateOptions[0].name;
        this.refreshTargetOptions();
        this.analyzeIfReady();
      },
      error: () => {
        this.loading = false;
        this.snackBar.open("Could not load the Tables Definition", null, { duration: 5000 });
      },
    });
  }

  // ---- table pickers ----
  private flattenedCount(tableId: number): number {
    return (this.definition.columns || []).filter((c) => c.admTableId === tableId && c.columnKind === 1 && !c.retired).length;
  }

  private refreshTableOptions() {
    const counted = (this.definition.tables || []).filter((t) => !t.retired).map((t) => ({ name: t.tableName, count: this.flattenedCount(t.admTableId) }));
    // Tables that already have flattened columns, the most first (the obvious template is at the top).
    this.templateOptions = counted.filter((t) => t.count > 0).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    this.refreshTargetOptions();
  }

  private refreshTargetOptions() {
    this.targetOptions = (this.definition.tables || [])
      .filter((t) => !t.retired && t.tableName !== this.templateTable)
      .map((t) => ({ name: t.tableName, count: this.flattenedCount(t.admTableId) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  onTemplateChange() {
    if (this.targetTable === this.templateTable) this.targetTable = null;
    this.refreshTargetOptions();
    this.analyzeIfReady();
  }

  analyzeIfReady() {
    this.analysis = null;
    this.rows = [];
    this.analyzeError = "";
    if (!this.templateTable || !this.targetTable) return;
    this.analyzing = true;
    this.transferService.adminAdmTransferAnalyzeFlattenCopy({ templateTable: this.templateTable, targetTable: this.targetTable }).subscribe({
      next: (analysis) => {
        this.analyzing = false;
        this.analysis = analysis;
        this.rows = (analysis.groups || []).map((g) => this.newRow(g));
      },
      error: (e) => {
        this.analyzing = false;
        this.analyzeError = this.errorText(e, "Could not analyze these tables");
      },
    });
  }

  private newRow(g: AdmFlattenCopyGroup): CopyRow {
    const row: CopyRow = {
          group: g,
      selected: false,
      expanded: false,
      joinIndex: g.defaultJoinIndex,
      connectColumn: "",
      fromPrefix: g.suggestedPrefix || "",
      toPrefix: g.suggestedPrefix || "",
      fieldGroup: g.suggestedFieldGroup || "",
      columns: new Set((g.columns || []).map((c) => c.name)),
      choices: [],
    };
    this.refreshChoices(row);
    // An unconnected default join point: pre-pick the obvious column.
    const attach = this.attachOf(row);
    if (!attach.connected && attach.suggestedColumns && attach.suggestedColumns.length > 0) row.connectColumn = attach.suggestedColumns[0];
    return row;
  }

  // ---- rows ----
  get visibleRows(): CopyRow[] {
    const q = this.groupSearch.trim().toLowerCase();
    return this.rows.filter(
      (r) => !q || (r.group.label || "").toLowerCase().includes(q) || (r.group.columns || []).some((c) => c.name.toLowerCase().includes(q))
    );
  }

  attachOf(row: CopyRow): AdmFlattenCopyAttach {
    return row.group.attach.find((a) => a.joinIndex === row.joinIndex) || row.group.attach[0];
  }

  // Columns of the target the admin can connect on, the ones that look right first.
  private refreshChoices(row: CopyRow) {
    const target = (this.definition.tables || []).find((t) => t.tableName === this.targetTable);
    if (!target) { row.choices = []; return; }
    const suggested = new Set(this.attachOf(row).suggestedColumns || []);
    row.choices = (this.definition.columns || [])
      .filter((c: AdmTableColumn) => c.admTableId === target.admTableId && c.columnKind === 0 && !c.retired)
      .map((c) => ({ name: c.columnName, suggested: suggested.has(c.columnName) }))
      .sort((a, b) => Number(b.suggested) - Number(a.suggested) || a.name.localeCompare(b.name));
  }

  onJoinChange(row: CopyRow) {
    row.connectColumn = "";
    this.refreshChoices(row);
    // A connected join point needs no column; for an unconnected one, offer the obvious column.
    const attach = this.attachOf(row);
    if (!attach.connected && attach.suggestedColumns && attach.suggestedColumns.length > 0) row.connectColumn = attach.suggestedColumns[0];
  }

  // The name a template column gets on the target (mirrors the server's rule).
  newName(row: CopyRow, name: string): string {
    const from = row.fromPrefix || "";
    const to = row.toPrefix || "";
    if (from.length > 0) return name.startsWith(from) ? to + name.substring(from.length) : name;
    return to + name;
  }

  toggleColumn(row: CopyRow, name: string, on: boolean) {
    on ? row.columns.add(name) : row.columns.delete(name);
  }

  // What is wrong with a selected row, if anything.
  problem(row: CopyRow): string {
    if (!row.selected) return "";
    if (row.columns.size === 0) return "Select at least one column.";
    const attach = this.attachOf(row);
    if (attach.tooDeep) return "This join point makes the chain too long; choose another.";
    if (!attach.connected && !row.connectColumn) return `Choose the column on ${this.targetTable} that holds ${attach.joinTable}.${attach.joinColumn}.`;
    return "";
  }

  get selectedRows(): CopyRow[] { return this.rows.filter((r) => r.selected); }
  get selectedColumnCount(): number { return this.selectedRows.reduce((n, r) => n + r.columns.size, 0); }
  get canBuild(): boolean { return this.selectedRows.length > 0 && this.selectedRows.every((r) => this.problem(r) === "") && !this.building; }

  selectAllConnected() {
    this.rows.forEach((r) => (r.selected = this.attachOf(r).connected && !this.attachOf(r).tooDeep));
  }

  clearSelection() {
    this.rows.forEach((r) => (r.selected = false));
  }

  // ---- build and hand over to the review ----
  build() {
    if (!this.canBuild) return;
    this.building = true;
    this.buildError = "";
    this.transferService
      .adminAdmTransferBuildFlattenCopy({
        templateTable: this.templateTable,
        targetTable: this.targetTable,
        selections: this.selectedRows.map((r) => ({
          groupKey: r.group.groupKey,
          joinIndex: r.joinIndex,
          connectColumn: this.attachOf(r).connected ? "" : r.connectColumn,
          fromPrefix: r.fromPrefix,
          toPrefix: r.toPrefix,
          fieldGroup: r.fieldGroup,
          columns: Array.from(r.columns),
        })),
      })
      .subscribe({
        next: (result) => {
          this.building = false;
          this.built = result;
          this.reviewing = true;
        },
        error: (e) => {
          this.building = false;
          this.buildError = this.errorText(e, "Could not build the copy");
        },
      });
  }

  backToSelection() {
    this.reviewing = false;
  }

  onImported() {
    this.imported.emit();
    this.load();
  }

  // After a successful copy: back to the start, keeping the tables so another group can be added.
  finish() {
    this.reviewing = false;
    this.built = null;
    this.analyzeIfReady();
  }

  private errorText(e: any, fallback: string): string {
    const body = e && e.error;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && (body.title || body.message)) return body.title || body.message;
    return fallback;
  }
}
